import { useEffect, useState } from "react";
import { Calculator, TrendingUp, PieChart } from "lucide-react";
import AdminShell, { Panel } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { api, getErrorMessage } from "@/lib/api";

const fmt = (v) =>
  Number(v || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const MODULE_LABELS = {
  spot: "Spot",
  p2p: "P2P",
  otc: "OTC",
  escrow: "Escrow",
};

export default function TdsReports() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get(`/api/admin/tds/reports?period=${period}`)
      .then(({ data: d }) => setData(d))
      .catch((err) => {
        setError(getErrorMessage(err));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period]);

  if (loading && !data) {
    return <AdminShell title="TDS reports"><div className="text-muted-foreground">Loading…</div></AdminShell>;
  }

  if (error && !data) {
    return (
      <AdminShell title="TDS reports">
        <div className="text-red-400 text-[13px]">{error}</div>
      </AdminShell>
    );
  }

  const { summary, daily_series, module_breakdown, period: periodDetail } = data;

  return (
    <AdminShell title="TDS reports" subtitle="GST/TDS style collection summary">
      <div className="flex gap-2 mb-4">
        {["daily", "monthly", "yearly"].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded-md text-[12px] font-semibold capitalize ${
              period === p ? "brand-gradient text-primary-foreground" : "bg-line text-muted-foreground"
            }`}
            data-testid={`tds-report-period-${p}`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Total TDS Collected" value={fmt(summary?.total_tds_inr)} icon={Calculator} variant="purple" />
        <KpiCard label="Transactions" value={String(summary?.transaction_count ?? 0)} icon={TrendingUp} variant="blue" />
        <KpiCard label={`${period} TDS`} value={fmt(periodDetail?.total_tds_inr)} icon={PieChart} variant="yellow" />
        <KpiCard label="Period count" value={String(periodDetail?.transaction_count ?? 0)} icon={TrendingUp} variant="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel className="p-5">
          <h3 className="text-[14px] font-semibold mb-3">Module-wise deductions</h3>
          <div className="space-y-2">
            {(module_breakdown || []).map((m) => (
              <div key={m.module} className="flex justify-between text-[13px]">
                <span className="text-muted-foreground">{MODULE_LABELS[m.module] || m.module}</span>
                <span className="tabular-nums">{fmt(m.total_tds_inr)} ({m.count} txns)</span>
              </div>
            ))}
            {!(module_breakdown || []).length && (
              <p className="text-muted-foreground text-[12px]">No deductions recorded yet.</p>
            )}
          </div>
        </Panel>

        <Panel className="p-5">
          <h3 className="text-[14px] font-semibold mb-3">Daily deductions (last 30 days)</h3>
          <div className="space-y-1 max-h-[280px] overflow-y-auto">
            {(daily_series || []).slice(-14).map((d) => (
              <div key={d.date} className="flex justify-between text-[12px]">
                <span className="text-muted-foreground">{d.date}</span>
                <span>{fmt(d.total_tds_inr)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel className="p-5 mt-4">
        <h3 className="text-[14px] font-semibold mb-2">Tax summary (audit)</h3>
        <p className="text-[12px] text-muted-foreground">
          All TDS rows are immutable in <code className="text-primary">tds_transactions</code> with linked
          wallet ledger entries for reconciliation.
        </p>
      </Panel>
    </AdminShell>
  );
}
